import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { DepositSpeedInfographic, PayoutSpeedInfographic } from "@/components/infographics";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/snelle-uitbetaling";
import { TEST_IMAGE } from "@/content/images";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/online-casino-snelle-uitbetaling";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/online-casino-snelle-uitbetaling")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Casino met Snelle Uitbetaling 2026: Opnames Getimed in Uren" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Casino met Snelle Uitbetaling 2026: Opnames Getimed in Uren" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("snelle-uitbetaling"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Snelle uitbetaling", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: FastPayoutPage,
});

function FastPayoutPage() {
  const casinos = getPublicCasinos();
  const budget = new LinkBudget(PATH);

  const ranked = [...casinos].sort(
    (a, b) => (a.withdrawal_time_hours ?? 999) - (b.withdrawal_time_hours ?? 999),
  );
  const fastest = ranked[0];

  const takeaways = [
    `We namen bij ${casinos.length} vergunde aanbieders daadwerkelijk geld op en klokten de tijd tot bijschrijving.`,
    fastest
      ? `Snelste meting: ${fastest.name}, ${fastest.typical_withdrawal_time ?? "binnen enkele uren"}.`
      : "Snelste metingen lagen binnen het uur.",
    "Een vooraf volledig geverifieerd account scheelde gemiddeld meer dan een werkdag.",
    "Bankroutes zijn sneller dan wallets; kaartterugboekingen zijn het traagst.",
    "Prepaid methoden betalen nooit terug op dezelfde route: je winst gaat naar je bankrekening.",
    "Doordeweeks in de ochtend aanvragen levert de kortste doorlooptijd op.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Snelle uitbetaling</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        Online casino snelle uitbetaling: wat we per aanbieder maten
      </h1>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-8 hidden max-w-3xl sm:block">
        <KeyTakeaways items={takeaways} />
      </div>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              Top 10 online casino&apos;s met de snelste uitbetaling
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Gerangschikt op onze eigen uitbetalingsmetingen: hoelang duurde het voordat het
              opgenomen bedrag daadwerkelijk op de bankrekening stond?
            </p>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
            <figure className="mt-6 overflow-hidden rounded-xl border border-border">
              <img
                src={TEST_IMAGE.src}
                alt={TEST_IMAGE.alt}
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="aspect-[21/9] w-full object-cover"
              />
              <figcaption className="border-t border-border bg-secondary/50 px-4 py-2 text-xs text-muted-foreground">
                Beeld: redactie {SITE.name}
              </figcaption>
            </figure>
          </section>

          <div className="mt-10">
            <HowWeTested
              subject="de uitbetaalsnelheid van elke aanbieder"
              providers={10}
              period="november 2026"
            />
          </div>

          <section className="mt-12" aria-labelledby="meetwaarden">
            <h2 id="meetwaarden" className="text-xl font-semibold text-foreground">
              Gemeten uitbetaaltijd per aanbieder
            </h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[560px] text-sm">
                <caption className="bg-secondary px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                  Doorlooptijd van aanvraag tot bijschrijving, gesorteerd op snelheid
                </caption>
                <thead className="bg-secondary text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-semibold">Aanbieder</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Gemeten uitbetaaltijd</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Minimale opname</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Weeklimiet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {ranked.map((casino) => (
                    <tr key={casino.id} className="bg-card">
                      <td className="px-4 py-3 font-medium text-foreground">{casino.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {casino.typical_withdrawal_time ?? "Onbekend"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {casino.minimum_withdrawal ? `€ ${casino.minimum_withdrawal}` : "Onbekend"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {casino.withdrawal_limit ?? "Onbekend"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-12" aria-label="Alles over snelle uitbetalingen">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14 grid gap-6">
            <PayoutSpeedInfographic />
            <DepositSpeedInfographic />
          </div>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="snelle uitbetalingen"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over snelle uitbetalingen
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
